import React from "react";
import styled from "styled-components";
import { Plus } from "react-feather";
import { Carousel } from "@cinesys/ui/components";

/** Shared by every Spotter screen with an insertable `{Token}` chip row (Fields' Formatting
 *  mode, Field Mapping's Conditional field picker, File Rename's per-category templates) — one
 *  definition so the chips read the same everywhere instead of drifting screen to screen.
 *  Callers still pass their own `Carousel` layout props (`gap`, `justify`, `arrowSize`,
 *  `fadeColor`, ...) since those vary by the space each screen gives the row. No margin of its
 *  own — always render it inside a `TokenRow` (below), which owns the spacing so a chip row and
 *  its `Banner placeholder=` "no options" fallback take up the exact same space. */
export const TokenCarousel = styled(Carousel)`
  flex: 1;
  min-width: 0;
`;

/** Wraps a `TokenCarousel` (or a `Banner placeholder=` fallback when nothing matches the
 *  customer's in-progress filter) at a fixed min-height matching a single row of chips — without
 *  this, swapping between "some chips" and "no options" changes the row's height and the layout
 *  visibly jumps. `align-items: center` also vertically centers the `Banner` fallback (whose own
 *  min-height is shorter than a chip) inside that fixed space. Callers whose parent container
 *  already spaces its children itself (e.g. a `gap`-based flex column) should override `margin`
 *  to `0` rather than double up. */
export const TokenRow = styled.div`
  min-height: 30px;
  display: flex;
  align-items: center;
  margin: 10px 0;
`;

const TokenButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  background: var(--spotterCodeBg);
  border: 1px solid var(--spotterFacialLine);
  color: var(--spotterInk);
  padding: 5px 10px;
  border-radius: 99px;
  cursor: pointer;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  white-space: nowrap;

  svg {
    color: var(--spotterFacial);
  }

  &:hover {
    border-color: var(--spotterFacial);
    background: var(--spotterFacialSoft);
    color: var(--spotterFacial);
  }
`;

interface TokenChipProps {
  token: string;
  onClick: () => void;
}

/** Purple (`--spotterFacial`, otherwise Glossary's mode color) rather than the brand amber —
 *  amber is already each screen's all-purpose accent (links, selection, the save button...), so
 *  a chip styled in it doesn't read as its own distinct "clickable" affordance. */
const TokenChip: React.FC<TokenChipProps> = ({ token, onClick }) => (
  <TokenButton type="button" onClick={onClick}>
    <Plus size={16} />
    {token}
  </TokenButton>
);

export default TokenChip;
